import classnames from 'classnames';
import React, {
    useCallback,
    FunctionComponent,
    HTMLAttributes,
    useRef,
    useEffect,
    useState,
} from 'react';

import SearchIcon from '@/app/assets/SearchIcon';
import { useI18n } from '@/app/hooks/useI18n';
import { suggestWidget } from '@/scenarios/suggest/widgets';
import { useWidget } from '@/shared/hooks/useWidget';

import styles from './_Navigation.module.css';
import { NavigationContent } from './_NavigationContent';
import { Tabs } from './_Tabs';

interface Props extends HTMLAttributes<HTMLDivElement> {
    isHeaderVisible: boolean;
}

export const Navigation: FunctionComponent<Props> = ({ isHeaderVisible }) => {
    const i18n = useI18n();
    const [searchVisible, setSearchVisible] = useState(false);

    const inputEl = useRef<HTMLInputElement>(null);
    const Suggest = useWidget(suggestWidget);

    useEffect(() => {
        if (inputEl && inputEl.current && searchVisible) {
            inputEl.current.focus();
        }
    });

    const handleClose = useCallback((): void => {
        setSearchVisible(false);
    }, [setSearchVisible]);

    const handleSearchButtonClick = useCallback(() => {
        setSearchVisible(true);
    }, [setSearchVisible]);

    useEffect(() => {
        if (!isHeaderVisible) {
            setSearchVisible(false);
        }
    }, [isHeaderVisible]);

    const searchButton = (
        <button
            className={styles['search-button']}
            aria-label={i18n('search_button_aria_label')}
            onClick={handleSearchButtonClick}
        >
            <SearchIcon className={styles.icon} />
        </button>
    );

    return (
        <div className={styles.contentWrapper}>
            <div
                className={classnames(styles['tabs-wrapper'], {
                    [styles['tabs-wrapper_hidden']]: searchVisible,
                })}
            >
                <NavigationContent
                    searchButton={searchButton}
                    navigation={
                        <nav aria-label={i18n('navigation_aria_label')}>
                            <Tabs isHeaderVisible={isHeaderVisible} searchVisible={searchVisible} />
                        </nav>
                    }
                />
            </div>

            <div
                className={classnames(styles['search-wrapper'], {
                    [styles['search-wrapper_hidden']]: !searchVisible,
                })}
            >
                <Suggest onClose={handleClose} inputRef={inputEl} searchVisible={searchVisible} />
                <button
                    data-test-id="SearchCloseButton"
                    className={styles['close-button']}
                    onClick={handleClose}
                    aria-label={i18n('search_close_button_aria_label')}
                />
            </div>
        </div>
    );
};
